import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Crown, Star, Ban } from 'lucide-react';
import { pricingPlans } from '../mock';
import { fetchSettings } from '../api';

export default function PricingCards() {
  const navigate = useNavigate();
  const [weeklyAvailable, setWeeklyAvailable] = useState(true);
  const [plans, setPlans] = useState(pricingPlans);

  useEffect(() => {
    (async () => {
      try {
        const s = await fetchSettings();
        setWeeklyAvailable(s.weeklyAvailable !== false);
        if (Array.isArray(s.plans) && s.plans.length) setPlans(s.plans);
      } catch (e) { /* default available + mock plans */ }
    })();
  }, []);

  return (
    <section id="pricing" className="section-fade py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="text-center">
          <p className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-orange-500">Plans & Pricing</p>
          <h2 className="mt-2 font-heading text-4xl font-extrabold text-white sm:text-5xl">Premium Single Tip Packages</h2>
          <p className="mx-auto mt-3 max-w-2xl text-neutral-400">One expertly analysed single match every day, delivered before kickoff. Choose the plan that fits your goals.</p>
        </div>
        <div className="mx-auto mt-12 grid max-w-4xl gap-8 md:grid-cols-2">
          {plans.map((p) => {
            const unavailable = p.id === 'weekly' && !weeklyAvailable;
            return (
              <div key={p.id} data-testid={`plan-card-${p.id}`} className={`card-dark relative flex flex-col rounded-2xl p-8 ${p.highlight ? 'ring-2 ring-orange-500' : ''} ${unavailable ? 'opacity-60 grayscale' : ''}`}>
                {unavailable && (
                  <span data-testid="plan-not-available-badge" className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-red-500 px-4 py-1 font-heading text-xs font-bold uppercase tracking-wide text-white">
                    <Ban className="h-3.5 w-3.5" /> Not Available
                  </span>
                )}
                {p.highlight && !unavailable && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-orange-500 px-4 py-1 font-heading text-xs font-bold uppercase tracking-wide text-[#15120f]">{p.badge}</span>
                )}
                <div className="flex items-center gap-2">
                  {p.highlight ? <Crown className="h-6 w-6 text-orange-500" /> : <Star className="h-6 w-6 text-orange-500" />}
                  <h3 className="font-heading text-2xl font-extrabold text-white">{p.name}</h3>
                </div>
                <p className="mt-2 text-sm text-neutral-400">{p.subtitle}</p>
                <div className="mt-6 flex items-end gap-1">
                  <span className="font-heading text-5xl font-extrabold orange-text-gradient">€{p.price}</span>
                  <span className="mb-2 text-sm text-neutral-500">/ {p.period}</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-neutral-300">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {unavailable ? (
                  <button disabled data-testid="plan-unavailable-btn" className="mt-8 w-full cursor-not-allowed rounded-lg border border-[#3a322a] bg-white/5 py-3.5 font-heading text-base font-bold uppercase tracking-wide text-neutral-500">
                    Not Available
                  </button>
                ) : (
                  <button onClick={() => navigate('/contact')} className="btn-orange mt-8 w-full rounded-lg py-3.5 font-heading text-base font-bold uppercase tracking-wide">
                    Request This Plan
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="mx-auto mt-6 max-w-2xl text-center text-xs text-neutral-500">To subscribe, contact us through the form and our team will reply with the next steps.</p>
      </div>
    </section>
  );
}
